import { QueryClient } from "@/compat/reactQuery";

// The ported tree was written for react-query v3, which took a positional
// queryKey: e.g. `invalidateQueries([key, id])`, `cancelQueries(queryKey)`.
// react-query v5 instead expects a *filters object* (`{ queryKey }`) and treats
// a bare array/string as "no queryKey" -> it then matches EVERY query and
// invalidates/cancels the whole cache. This subclass adapts the positional form
// back to `{ queryKey }` so the ported call sites keep their targeted behaviour
// without being rewritten one-by-one.
//
// Only the filter-based methods are adapted. `getQueryData`/`setQueryData` are
// unchanged in v5 (still positional queryKey), so they are intentionally NOT
// overridden. A second positional arg (options) is passed straight through.
const toFilters = (filters: any) =>
  Array.isArray(filters)
    ? { queryKey: filters }
    : typeof filters === "string" || typeof filters === "number"
      ? { queryKey: [filters] }
      : filters;

class CompatQueryClient extends QueryClient {
  invalidateQueries(filters?: any, options?: any) {
    return super.invalidateQueries(toFilters(filters), options);
  }
  cancelQueries(filters?: any, options?: any) {
    return super.cancelQueries(toFilters(filters), options);
  }
  refetchQueries(filters?: any, options?: any) {
    return super.refetchQueries(toFilters(filters), options);
  }
  removeQueries(filters?: any) {
    return super.removeQueries(toFilters(filters));
  }
  resetQueries(filters?: any, options?: any) {
    return super.resetQueries(toFilters(filters), options);
  }
}

// Typed as `any` so the ported v3-style positional calls type-check against the
// underlying v5 QueryClient without rewriting call sites.
const client: any = new CompatQueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const reactQueryUtils = {
  client,
};

export default reactQueryUtils;
